import React, { Fragment, useEffect, useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'

import { useModals } from '../../../../providers/ModalsProvider'

import {
  processOrder,
  redeemPromoCode,
  removePromoCode,
} from '../../../../../../../utilities/api'

import {
  formatCurrency,
  formatNumber,
  getSinglarOrPluralWord,
  getProcessingFeeFromChargeAmount,
  isCartExpiredResponse,
} from '../../../../../../../utilities/helpers'
import {
  getPackageFlatServiceFee,
  isTrustedWebsitePackageFeeSnapshot,
} from '@blocktickets/shared-utils'

import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'

import { Spinner } from '../../../../../../LoadingContainer/Spinner'
import { InfoIcon } from '../../../../../../InfoIcon'
import { MarketingOptIn } from '../../../MarketingOptIn'
import authService from '../../../../../../../utilities/services/auth.service'
import { readTrackingCode } from '../../../../../../../utilities/trackingLink'

import './checkoutBottom.scss'
import { useTimeout } from '../../../../providers/TimeoutProvider/TimeoutProvider'

const endsWithPunctuation = (text) => /[.!?]$/.test(String(text || '').trim())

const formatPromoCodeErrorMessage = (message) => {
  const trimmed = String(message || '').trim()
  if (!trimmed) return 'Please try again.'
  const separator = endsWithPunctuation(trimmed) ? ' ' : '. '
  return `${trimmed}${separator}Please try again.`
}

// This is almost identical to frontend/src/components/PopupCard/PopupCard.js in terms of jsx
// but pretty much identical to TotalCard in terms of functions and state
export default function CheckoutBottom({
  setStatus,
  intentId,
  pendingOrderId,
  paymentDeclined,
  cart,
  donationPricing,
  donationRequirementMet = true,
  isRefreshingIntent = false,
}) {
  const { openModal } = useModals()
  const { isPurchasing, setIsPurchasing, handleCartExpired } = useTimeout()
  const [discountedPrice, setDiscountedPrice] = useState()
  const [originalTotal, setOriginalTotal] = useState()
  const [codeError, setCodeError] = useState({ hasError: false, message: '' })

  const stripe = useStripe()
  const elements = useElements()
  const isPackage = Boolean(cart?.package)
  const isAccessPass = Boolean(cart?.access_pass_template)

  const [promoCode, setPromoCode] = useState()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isRemoving, setIsRemoving] = useState(false)

  const [isDiscountApplied, setIsDiscountApplied] = useState(false)
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)
  const showMarketingOptIn = authService.isLoggedIn()
  const checkoutOrganization =
    cart?.event?.organization ||
    cart?.package?.organization ||
    cart?.flex_pack?.organization ||
    cart?.access_pass_template?.organization
  const organizationName = checkoutOrganization?.name
  const optInMessage = checkoutOrganization?.opt_in_message

  useEffect(() => {
    if (codeError?.hasError) {
      setCodeError({ hasError: false, message: '' })
    }
  }, [promoCode])

  const completePurchase = () => {
    if (!donationRequirementMet || isRefreshingIntent) return
    setIsPurchasing(true)
    let data = {
      cart: cart,
      paymentIntentId: intentId,
      pendingOrderId,
      trackingCode: readTrackingCode(cart?.eventUUID || cart?.event?.uuid),
      ...(showMarketingOptIn ? { marketingEmailOptIn } : {}),
    }

    processOrder(data)
      .then((res) => {
        sessionStorage.setItem('order', JSON.stringify({ id: res.data?.id }))
        sendPayment()
      })
      .catch((err) => {
        console.log(err)
        setIsPurchasing(false)
        if (isCartExpiredResponse(err)) {
          handleCartExpired()
        }
        // setHasError(true);
        // setErrorShow({
        //   error: true,
        //   msg: err.response?.data?.error?.message,
        //   remaining: err.response?.data?.error?.details?.ticketsRemaining,
        // });
      })
  }

  const sendPayment = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: '',
      },
      redirect: 'if_required',
    })

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setIsPurchasing(false)
      paymentDeclined(error.message)
      console.warn(error.message)
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
      setStatus('successful')
    }
  }

  const submitPromoCode = (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    let data = {}
    data.code = promoCode
    data.paymentIntentId = intentId
    data.cart = cart

    redeemPromoCode(data)
      .then((res) => {
        setIsDiscountApplied(true)
        setDiscountedPrice(res.data?.promoPricingDetails?.discountedPrice)
        setOriginalTotal(res?.data?.promoPricingDetails?.originalPrice)
        setPromoCode(res.data)
        setIsSubmitting(false)
      })
      .catch((err) => {
        /*
        Errors messages are 
        1. Promo code not found
        2. Promo code already applied to order
        3. Promo code no longer valid
        4. Stripe error - minimum order total not met
        */
        console.error(err)
        setIsSubmitting(false)
        setCodeError({
          hasError: true,
          message: err?.response?.data?.error?.message,
        })
      })
  }

  // remove promo code
  const handleRemove = () => {
    setIsRemoving(true)
    let data = {}
    data.paymentIntentId = intentId
    data.cart = cart

    removePromoCode(data)
      .then((res) => {
        setPromoCode()
        setIsDiscountApplied(false)
        setIsRemoving(false)
      })
      .catch((err) => {
        console.error(err)
        setIsRemoving(false)
      })
  }

  const packageEventCountFromTickets = Array.isArray(cart?.tickets)
    ? new Set(
        cart.tickets
          .map((ticket) => ticket.eventUUID || ticket.eventId)
          .filter(Boolean)
      ).size
    : 0
  let count = isPackage
    ? cart?.package?.events?.length || packageEventCountFromTickets
    : cart?.tickets?.length

  count = cart?.flex_pack ? cart?.flex_pack?.gameTickets : count
  count = isAccessPass ? 1 : count

  const packageWebsiteFeeSnapshot = cart?.packageWebsiteFeeSnapshot
  const packageUsesOrgFeeConfig = isTrustedWebsitePackageFeeSnapshot(
    packageWebsiteFeeSnapshot
  )
  const packageFlatServiceFee = getPackageFlatServiceFee(cart?.package)
  const packageOrgServiceFee = Number(
    packageFlatServiceFee !== null
      ? packageFlatServiceFee
      : (packageWebsiteFeeSnapshot?.serviceFee ?? cart?.serviceFee ?? 0)
  )
  const packageOrgProcessingFee = Number(
    packageWebsiteFeeSnapshot?.processingFee ?? cart?.processingFee ?? 0
  )
  const packageCustomFeeLines = Array.isArray(
    packageWebsiteFeeSnapshot?.customFeeLines
  )
    ? packageWebsiteFeeSnapshot.customFeeLines.filter(
        (line) => Number(line?.amount) > 0
      )
    : Number(packageWebsiteFeeSnapshot?.customFeeAmount) > 0
      ? [
          {
            name: packageWebsiteFeeSnapshot?.customFeeName || 'Custom fee',
            amount: Number(packageWebsiteFeeSnapshot.customFeeAmount),
          },
        ]
      : []

  const packageBaseTotal = Number(originalTotal ?? cart?.total ?? 0)
  const packageStoredProcessingFee = Number(cart?.processingFee || 0)
  const packageHasStoredProcessingFee =
    isPackage && packageStoredProcessingFee > 0
  const packageProcessingFee = isPackage
    ? packageUsesOrgFeeConfig
      ? packageOrgProcessingFee
      : packageHasStoredProcessingFee && !isDiscountApplied
        ? packageStoredProcessingFee
        : getProcessingFeeFromChargeAmount(packageBaseTotal)
    : 0
  const packageDiscountedProcessingFee =
    isPackage && isDiscountApplied
      ? getProcessingFeeFromChargeAmount(discountedPrice || 0)
      : 0
  const packageOriginalDisplayTotal = isPackage
    ? packageUsesOrgFeeConfig
      ? packageBaseTotal
      : packageHasStoredProcessingFee
        ? packageBaseTotal
        : packageBaseTotal + packageProcessingFee
    : packageBaseTotal
  const donationAmount = Number(donationPricing?.donationAmount || 0)
  const donationChargeTotal = Number(donationPricing?.totalCharge || 0)
  const flexOriginalTotal =
    (originalTotal ?? cart?.total) + cart?.totalTax + cart?.processingFee
  const accessPassOriginalTotal =
    (originalTotal ?? cart?.total) +
    (cart?.totalTax || 0) +
    (cart?.processingFee || 0) +
    (Number(cart?.serviceFee) || 0)
  const eventOriginalTotal =
    (originalTotal ?? cart?.total) + donationChargeTotal
  const packageDiscountedDisplayTotal =
    isPackage && isDiscountApplied
      ? (discountedPrice || 0) + packageDiscountedProcessingFee
      : 0
  const packageDisplayedProcessingFee = isPackage
    ? packageUsesOrgFeeConfig
      ? packageOrgProcessingFee
      : isDiscountApplied
        ? packageDiscountedProcessingFee
        : packageProcessingFee
    : 0

  return (
    <>
      {isDiscountApplied ? (
        <div className="mt-4">
          <h6 className="form-label">Promo Code</h6>
          <Card className="card--white">
            <Card.Body className="px-3 py-2">
              <Stack direction="horizontal" gap={3} className="discount">
                <Stack>
                  <span className="small small-bold d-flex gap-1">
                    <span>{promoCode?.promoPricingDetails?.code}</span>
                    <span>
                      (-
                      {formatCurrency(
                        promoCode?.promoPricingDetails?.amountDiscounted
                      )}
                      )
                    </span>
                  </span>
                  <span className="caption text-muted">Promo code applied</span>
                </Stack>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleRemove}
                  disabled={isRemoving}
                  className={`icon-button icon-button--danger btn-width-sm text-danger m-0 ${!isRemoving ? 'btn-remove' : ''} `}
                >
                  {isRemoving ? <Spinner variant="dark" /> : 'Remove'}
                </Button>
              </Stack>
            </Card.Body>
          </Card>
        </div>
      ) : (
        <>
          {!cart.flex_pack &&
            !isAccessPass && ( // promo codes dont apply to flex packs / access passes
              <>
                <Form
                  className="mt-4 d-flex gap-4 align-items-end"
                  onSubmit={submitPromoCode}
                >
                  <Form.Group
                    className="form-group flex-grow-1"
                    controlId="code"
                  >
                    <Form.Label>Promo code</Form.Label>
                    <Form.Control
                      type="text"
                      name="code"
                      placeholder="Enter promo code"
                      value={promoCode || ''}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className={`${codeError?.hasError ? 'error-border' : ''}`}
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    size="lg"
                    variant="outline-light"
                    disabled={!promoCode || isSubmitting || codeError?.hasError}
                    className="icon-button m-0 btn-width-xs"
                  >
                    {isSubmitting ? <Spinner variant="dark" /> : 'Apply'}
                  </Button>
                </Form>
                {codeError?.hasError && (
                  <Alert variant="danger" className="gap-3 mt-3">
                    <div className="body">
                      <p>{formatPromoCodeErrorMessage(codeError?.message)}</p>
                    </div>
                  </Alert>
                )}
              </>
            )}
        </>
      )}
      {showMarketingOptIn && (
        <MarketingOptIn
          checked={marketingEmailOptIn}
          onChange={setMarketingEmailOptIn}
          organizationName={organizationName}
          optInMessage={optInMessage}
          disabled={isPurchasing || isRefreshingIntent}
        />
      )}
      <div className="checkout-bottom">
        <Stack gap={1}>
          <Stack direction="horizontal" gap={2}>
            <span className="small small-bold">
              Total for {formatNumber(count)}{' '}
              <span>
                {getSinglarOrPluralWord(
                  count,
                  cart?.flex_pack
                    ? 'Voucher'
                    : isAccessPass
                      ? 'Pass'
                      : isPackage
                        ? 'event'
                        : 'ticket'
                )}
              </span>
            </span>
            {!isPackage && (
              <Button
                variant="default"
                className="p-0 m-0"
                onClick={() =>
                  openModal(
                    'Ticket Information',
                    isDiscountApplied
                      ? {
                          ...promoCode,
                          am_pricing_objects:
                            promoCode?.am_pricing_objects ||
                            cart?.am_pricing_objects,
                        }
                      : cart
                  )
                }
              >
                <InfoIcon />
              </Button>
            )}
          </Stack>
          <Stack direction="horizontal" gap={2}>
            <h6
              className={`fs-md mb-0 ${isDiscountApplied ? 'normal text-muted text-decoration-line-through' : ''} `}
            >
              {formatCurrency(
                cart?.flex_pack
                  ? flexOriginalTotal + donationChargeTotal
                  : isAccessPass
                    ? accessPassOriginalTotal + donationChargeTotal
                    : isPackage
                      ? packageOriginalDisplayTotal + donationChargeTotal
                      : eventOriginalTotal
              )}
            </h6>
            {isDiscountApplied && (
              <h6 className="fs-md mb-0">
                {formatCurrency(
                  cart?.flex_pack
                    ? discountedPrice + promoCode?.totalTax
                    : isPackage
                      ? packageDiscountedDisplayTotal
                      : discountedPrice
                )}
              </h6>
            )}
          </Stack>
          {cart?.flex_pack &&
            packageCustomFeeLines.map((line) => (
              <span
                className="caption text-muted d-block"
                key={`${line.id || line.name}-${line.amount}`}
              >
                Includes {formatCurrency(line.amount)} {line.name}
              </span>
            ))}
          {donationAmount > 0 && (
            <span className="caption text-muted d-block">
              Includes {formatCurrency(donationAmount)} donation
            </span>
          )}
          {!donationRequirementMet && (
            <span className="caption text-danger d-block">
              Add the required donation before completing checkout.
            </span>
          )}
          {isPackage &&
            (packageUsesOrgFeeConfig || packageOrgServiceFee > 0) && (
              <>
                {packageOrgServiceFee > 0 && (
                  <span className="caption text-muted d-block">
                    Includes {formatCurrency(packageOrgServiceFee)} season
                    package fee
                  </span>
                )}
                {packageDisplayedProcessingFee > 0 && (
                  <span className="caption text-muted d-block">
                    Includes {formatCurrency(packageDisplayedProcessingFee)}{' '}
                    payment processing fee
                  </span>
                )}
                {packageCustomFeeLines.map((line) => (
                  <span
                    className="caption text-muted d-block"
                    key={`${line.id || line.name}-${line.amount}`}
                  >
                    Includes {formatCurrency(line.amount)} {line.name}
                  </span>
                ))}
              </>
            )}
          {isPackage &&
            !packageUsesOrgFeeConfig &&
            packageOrgServiceFee <= 0 && (
              <span className="caption text-muted">
                Includes {formatCurrency(packageDisplayedProcessingFee)}{' '}
                processing fee
                {packageCustomFeeLines.map((line) => (
                  <span
                    className="d-block"
                    key={`${line.id || line.name}-${line.amount}`}
                  >
                    Includes {formatCurrency(line.amount)} {line.name}
                  </span>
                ))}
              </span>
            )}
        </Stack>

        {/* Wrap the 2 buttons so the space-between works */}
        <div>
          <div id="payment-request-button">
            {/* Need to rethink how payment element and totalCard manage state */}
            {/* after payment has been entered  */}
            <Button
              id="payment-btn"
              variant="primary"
              size="lg"
              disabled={
                isPurchasing || !donationRequirementMet || isRefreshingIntent
              }
              className="icon-button w-100 d-none mt-0"
              onClick={completePurchase}
            >
              {isPurchasing ? (
                <Fragment>
                  <Spinner />
                  <span>Purchasing...</span>
                </Fragment>
              ) : (
                <span>Submit Payment</span>
              )}
            </Button>
          </div>

          <div id="payment-request-button">
            {/* Need to rethink how payment element and totalCard manage state */}
            {/* before payment has been entered */}
            <Button
              id="payment-btn-disabled"
              disabled
              variant="primary"
              size="lg"
              className="icon-button w-100 mt-0 btn-width btn-width-xs"
            >
              <span>Submit Payment</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
